/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Resource } from '@ngxp/rest';
import { Observable, filter, map, tap } from 'rxjs';
import { StateResource, hasStateResourceError, isLoaded } from './resource.util';

export function filterIsLoadedOrHasError<T>(): (source: Observable<StateResource<T>>) => Observable<StateResource<T>> {
  return (source: Observable<StateResource<T>>): Observable<StateResource<T>> => {
    return source.pipe(
      filter((stateResource: StateResource<T>) => hasStateResourceError(stateResource) || isLoaded(stateResource)),
    );
  };
}

export function mapToResource<T>(): (source: Observable<StateResource<T>>) => Observable<T> {
  return (source: Observable<StateResource<T>>): Observable<T> => {
    return source.pipe(map((stateResource: StateResource<T>) => stateResource.resource));
  };
}

export function mapToFirst<T extends Resource, B extends Resource>(): (
  source: Observable<[StateResource<T>, B]>,
) => Observable<StateResource<T>> {
  return (source: Observable<[StateResource<T>, B]>): Observable<StateResource<T>> => {
    return source.pipe(map(([stateResource]) => stateResource));
  };
}

export function tapOnLoadedResource<T>(
  runnable: (commandStateResource: StateResource<T>) => void,
): (source: Observable<StateResource<T>>) => Observable<StateResource<T>> {
  return (source: Observable<StateResource<T>>): Observable<StateResource<T>> => {
    return source.pipe(
      tap((stateResource: StateResource<T>) => {
        if (isLoaded(stateResource)) runnable(stateResource);
      }),
    );
  };
}
